import React, { useContext, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
  CredentialListView,
  CredentialOrderDetailsView,
  UserCredentialListView,
} from '../../../../domain/credentialView';
import '../CredentialDetailsModal.css';
import closeIcon from '../../../../assets/closeIcon.png';
import {
  ParsedBackendError,
  resendEmailCredentialOrder,
} from '../../../../services/vidcredentials';
import { Popup } from '../../../../components/Popup/Popup';
import { AllCredentialsContext } from '../../../../contexts/CredentialsProviders/Providers/AllCredentialsContext';
import { UserCredentialsContext } from '../../../../contexts/CredentialsProviders/Providers/UserCredentialsContext';
import { renderChildren } from '../helpers/RenderFunctions';
import { formatDate, transformCamelCase } from '../helpers/StringHelpers';
import { useTranslation } from 'react-i18next';

interface Props {
  credential: CredentialOrderDetailsView;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const CredentialOrderModal = (props: Props) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { credentials } = useContext(AllCredentialsContext);
  const userCredentials = useContext(UserCredentialsContext).credentials;

  const closeModal = (e: any) => {
    e.preventDefault();
    props.setIsOpen(false);
  };

  const getCredential = (
    issuanceId: string,
  ): CredentialListView | undefined => {
    let credentialListView:
      | CredentialListView[]
      | UserCredentialListView[]
      | undefined;
    credentialListView = credentials;
    if (
      props.credential.types.some(
        (type) => type.toUpperCase() === 'VIDCREDENTIALSTUDIO',
      )
    )
      credentialListView = userCredentials;
    return credentialListView.find(
      (credential) => credential.issuanceId === issuanceId,
    );
  };
  const resendEmail = async () => {
    try {
      const credentialView = getCredential(props.credential.issuanceId);
      if (!credentialView) {
        console.log('Could not find credential');
        return;
      }
      await resendEmailCredentialOrder(credentialView.resendURL ?? '');
      // TODO: Reload Table?
      setSuccessMessage(t('details.emailResentSuccess'));
      setErrorMessage('');
    } catch (error) {
      setErrorMessage((error as ParsedBackendError).title);
      setSuccessMessage('');
    }
  };

  return (
    <Modal isOpen={props.isOpen} centered={true} fullscreen="lg" size="lg">
      <ModalHeader
        close={
          <a href="#" onClick={(e) => closeModal(e)}>
            <img alt="Close icon" src={closeIcon} className="iconClose" />
          </a>
        }
        toggle={() => props.setIsOpen(false)}
        className="modalHeader"
      >
        {t('details.credentialDetails')}
      </ModalHeader>
      <ModalBody className="modalBody">
        <table className="tableModal">
          <thead>
            <tr>
              <td style={{ backgroundColor: 'white' }}>&nbsp;</td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">{t('details.credentialIssuanceId')}:</th>
              <td data-testid="type">{props.credential.issuanceId}</td>
            </tr>
            <tr>
              <th scope="row">{t('details.type')}:</th>
              <td data-testid="type">{props.credential.types}</td>
            </tr>
            <tr>
              <th scope="row">{t('details.issuer')}:</th>
              <td>{props.credential.issuer}</td>
            </tr>
            <tr>
              <th scope="row">{t('details.holder')}:</th>
              <td data-testid="holder">{props.credential.holder}</td>
            </tr>
            <tr>
              <th scope="row">{t('table.status')}:</th>
              <td data-testid="status">
                {t(`status.${props.credential.status}`)}
              </td>
            </tr>
            <tr>
              <th scope="row">{t('table.ivmStatus')}:</th>
              <td data-testid="ivm-status">
                {t(
                  `identityVerificationStatus.${props.credential.identityVerificationMethodStatus}`,
                )}
              </td>
            </tr>
            <tr>
              <th scope="row">{t('details.createdAt')}:</th>
              <td data-testid="created-at">
                {formatDate(props.credential.createdAt)}
              </td>
            </tr>
            <tr>
              <td style={{ backgroundColor: 'white' }}>&nbsp;</td>
            </tr>
          </tbody>
        </table>
        {props.credential.payload &&
          Object.entries(props.credential.payload).map(([key, value]) => {
            return (
              <div key={key}>
                <h2 className="attributeHeader">{transformCamelCase(key)}</h2>
                {renderChildren(value, {})}
                <tr>&nbsp;</tr>
              </div>
            );
          })}
      </ModalBody>
      <ModalFooter>
        <div className="buttons-container">
          {props.credential.status === 'VERIFIED' && (
            <>
              <Button className="buttonResend" onClick={resendEmail}>
                {t('actions.resend')}
              </Button>{' '}
            </>
          )}
        </div>
        {errorMessage && (
          <Popup
            error
            message={errorMessage}
            onClose={() => setErrorMessage('')}
          />
        )}
        {successMessage && (
          <Popup
            success
            message={successMessage}
            onClose={() => setSuccessMessage('')}
          />
        )}
      </ModalFooter>
    </Modal>
  );
};
